import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  UiButton,
  UiDialogActions,
  UiDialogContent,
  UiDialogTitle,
  UiIcon,
} from 'ui-components';
import { toast } from 'lib';
import { usePermission } from 'hooks';
import ExportBulletinContentView from 'components/export-bulletin-content/ExportBulletinContentView';
import {
  fetchBulletinListForExport,
  addContentToBulletinByIds,
  openBulletinContentQueryModal,
  closeBulletinContentQueryModal,
  resetBulletinContentQueryForm,
  setBulletinContentQueryStep,
} from 'store';
import {
  StyledSendToBulletin,
  StyledSendToBulletinDialog,
} from './SendToBulletin.styles';

const SendToBulletin = ({ ids, platform, children, updateData = () => {} }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { userPermission } = usePermission();
  const [open, setOpen] = useState(false);

  const activeStep = useSelector(
    (state) => state.bulletinContentQueryReducer.activeStep
  );
  const selectedBulletinId = useSelector(
    (state) => state.bulletinContentQueryReducer.selectedBulletinId
  );
  const bulletinList = useSelector(
    (state) => state.bulletinContentQueryReducer.bulletinList
  );
  const submit = useSelector(
    (state) => state.bulletinContentQueryReducer.submit
  );
  const createBulletin = useSelector(
    (state) => state.bulletinContentQueryReducer.createBulletin
  );

  const { permission: permiAddToBulletin } = useMemo(
    () => userPermission('Bulletin', 'addToBulletin'),
    [userPermission]
  );

  const { permission: permiBulletinList } = useMemo(
    () => userPermission('Bulletin', 'index'),
    [userPermission]
  );

  useEffect(() => {
    if (!open) {
      dispatch(closeBulletinContentQueryModal());
      return undefined;
    }

    dispatch(resetBulletinContentQueryForm());
    dispatch(openBulletinContentQueryModal({ platform }));

    dispatch(fetchBulletinListForExport())
      .unwrap()
      .catch(() => {
        toast({
          title: t('common:error'),
          description: t('content:error.bulletinExport.fetchBulletinList'),
          type: 'error',
        });
      });

    return undefined;
  }, [dispatch, open, platform, t]);

  const handleClose = () => {
    setOpen(false);
  };

  const getSelectedBulletin = () =>
    bulletinList.data?.find((item) => item.id === selectedBulletinId);

  const handleSubmit = async () => {
    try {
      await dispatch(
        addContentToBulletinByIds({
          platform,
          bulletinId: selectedBulletinId,
          ids,
        })
      ).unwrap();

      const bulletin = getSelectedBulletin();
      handleClose();
      updateData();
      toast({
        title: t('content:success.bulletinExport.title'),
        description: t('content:success.bulletinExport.description'),
        type: 'success',
        action: (
          <div className='actions'>
            <UiButton variant='text'>
              <Link
                to={`/bulletins/${bulletin?.id}/${platform}`}
                state={{
                  title: bulletin?.title
                    ?.replaceAll(' ', '_')
                    .replaceAll('/', '-'),
                }}
              >
                {t('content:bulletinExport.viewBulletin')}
              </Link>
            </UiButton>
            {permiBulletinList && (
              <UiButton variant='text'>
                <Link to='/bulletins'>
                  {t('content:bulletinExport.bulletinList')}
                </Link>
              </UiButton>
            )}
          </div>
        ),
      });
    } catch (error) {
      // Error toast is shown in addContentToBulletinByIds
    }
  };

  const isCreateStep = activeStep === 'createBulletin';
  const showBack = isCreateStep && bulletinList.data?.length > 0;
  const dialogError =
    bulletinList.error || submit.error || createBulletin.error;

  if (!permiAddToBulletin) return null;

  return (
    <StyledSendToBulletin>
      {children(setOpen)}
      <StyledSendToBulletinDialog
        open={open}
        onClose={handleClose}
        className='send-to-bulletin-dialog'
      >
        <UiDialogTitle
          className='header send-to-bulletin-title'
          error={dialogError}
        >
          <div className='title-content'>
            {showBack && (
              <UiButton
                variant='text'
                className='title-back-link'
                onClick={() =>
                  dispatch(setBulletinContentQueryStep('selectBulletin'))
                }
                data-testid='send-to-bulletin-back-btn'
              >
                <UiIcon icon='ri-arrow-right-line' color='#0C395A' size={20} />
              </UiButton>
            )}
            <div className='title-text'>
              {isCreateStep
                ? t('content:bulletinExport.createBulletin')
                : t('content:bulletinExport.title')}
            </div>
          </div>
          <div onClick={handleClose} className='flex'>
            <UiIcon icon='ri-close-line' color='#454545' />
          </div>
        </UiDialogTitle>

        <UiDialogContent className='send-to-bulletin-body'>
          <ExportBulletinContentView showCount={false} />
        </UiDialogContent>

        {!isCreateStep && (
          <UiDialogActions className='send-to-bulletin-actions'>
            <UiButton
              variant='contained'
              onClick={handleSubmit}
              loading={submit.loading}
              disabled={submit.loading || !selectedBulletinId}
              data-testid='send-to-bulltetin-modal-submit-btn'
            >
              {t('content:bulletinExport.submit')}
            </UiButton>
          </UiDialogActions>
        )}
      </StyledSendToBulletinDialog>
    </StyledSendToBulletin>
  );
};

SendToBulletin.propTypes = {
  ids: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.array,
  ]),
  platform: PropTypes.string,
  children: PropTypes.any,
  updateData: PropTypes.func,
};

export default SendToBulletin;
